import axios from "axios";

const FALLBACK_BACKEND_URL =
  typeof window !== "undefined" ? window.location.origin : "";
const BACKEND_URL = (
  process.env.REACT_APP_BACKEND_URL || FALLBACK_BACKEND_URL
).replace(/\/$/, "");
const AUTH_STORAGE_KEY = "saudi-luxury-real-estate-auth";

// Token refresh state to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshSuccess = (newSession) => {
  refreshSubscribers.forEach((callback) => callback(newSession));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  timeout: 20000,
});

// Response interceptor for automatic token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only handle 401 errors and avoid infinite loops
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Skip refresh for auth endpoints themselves
    if (
      originalRequest.url?.includes("/auth/login") ||
      originalRequest.url?.includes("/auth/register") ||
      originalRequest.url?.includes("/auth/refresh")
    ) {
      return Promise.reject(error);
    }

    // Check if we have a stored session
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) {
      return Promise.reject(error);
    }

    let session;
    try {
      session = JSON.parse(stored);
    } catch {
      return Promise.reject(error);
    }

    if (!session?.access_token) {
      return Promise.reject(error);
    }

    // If already refreshing, wait for that to complete
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        addRefreshSubscriber((newSession) => {
          if (newSession) {
            originalRequest.headers.Authorization = `Bearer ${newSession.access_token}`;
            originalRequest.headers["X-CSRF-Token"] = newSession.csrf_token;
            originalRequest._retry = true;
            resolve(api(originalRequest));
          } else {
            reject(error);
          }
        });
      });
    }

    // Start refresh process
    isRefreshing = true;
    originalRequest._retry = true;

    try {
      const refreshResponse = await axios.post(
        `${BACKEND_URL}/api/auth/refresh`,
        {},
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
          timeout: 10000,
        }
      );

      const refreshData = refreshResponse.data;

      // Update stored session with new tokens
      const updatedSession = {
        ...session,
        access_token: refreshData.access_token,
        csrf_token: refreshData.csrf_token,
        expires_at: refreshData.expires_at,
      };

      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(updatedSession));

      // Notify all waiting requests
      onRefreshSuccess(updatedSession);

      // Retry original request with new token
      originalRequest.headers.Authorization = `Bearer ${updatedSession.access_token}`;
      originalRequest.headers["X-CSRF-Token"] = updatedSession.csrf_token;

      return api(originalRequest);
    } catch (refreshError) {
      // Refresh failed - clear session and notify
      localStorage.removeItem(AUTH_STORAGE_KEY);
      onRefreshSuccess(null);

      // Extract error message - handle both string and object responses
      let errorMessage = "Session expired. Please login again.";
      const detail = refreshError.response?.data?.detail;
      if (typeof detail === "string") {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        errorMessage = detail[0].msg;
      }

      // Dispatch custom event for session expiry notification
      window.dispatchEvent(
        new CustomEvent("session-expired", {
          detail: {
            message: errorMessage,
          },
        })
      );

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export const buildAuthHeaders = (session) => {
  if (!session?.access_token) {
    return {};
  }

  return {
    Authorization: `Bearer ${session.access_token}`,
    "X-CSRF-Token": session.csrf_token,
  };
};

export const resolveImageUrl = (source) => {
  if (!source) {
    return "";
  }

  if (source.startsWith("http://") || source.startsWith("https://")) {
    return source;
  }

  return `${BACKEND_URL}${source}`;
};
